import { useContext, useEffect, useState } from 'react';

import { FAQItem, FAQSection, PhoneCallIcon } from '@selina-finance/ui';
import mixpanel from 'mixpanel-browser';
import { useNavigate } from 'react-router-dom';

import { BookingCalendly } from '@Components/Calendly';
import {
    FinaliseAdvisorState,
    getFinaliseDevice,
    getFinaliseTrackingProperties,
} from '@Shared/helpers/finaliseAnalytics';
import { isWithinFinaliseCallHours } from '@Shared/helpers/finaliseBusinessHours';
import { ApplicationContext } from '@src/app/contexts/Application';
import { useCallBookingVariant } from '@src/app/hooks/useCallBookingVariant';
import { ExperimentsConfig } from '@src/app/utils/experiments';
import { KAMELEOON_FEATURE } from '@src/app/utils/kameleoon';
import BackButton from '@src/ui/components/BackButton/BackButton';
import { BookCallButton } from '@src/ui/components/BookCallButton';
import TrustpilotReviews from '@src/ui/components/TrustpilotReviews/TrustpilotReviews';

export const ADVISOR_PHONE_NUMBER = '020 4525 8044';
export const ADVISOR_PHONE_NUMBER_TEL = ADVISOR_PHONE_NUMBER.replace(/\s/g, '');

export const faqItems: FAQItem[] = [
    {
        id: 'what_call_about',
        question: 'What will the advice call be about?',
        answer: [
            'Your advisor reviews your options and makes sure your chosen offer is right for you and your budget. It’s also your chance to ask any questions.',
            'If you’re happy, they can take your application forward on the call.',
        ],
    },
    {
        id: 'why_needed',
        question: 'Why is the advice call needed?',
        answer: [
            'Every Selina customer has one. It’s how we make sure you’ve seen all your options and chosen the best one for your needs.',
        ],
    },
    {
        id: 'how_long',
        question: 'How long will it take?',
        answer: ['About 30-45 minutes, depending on your circumstances.'],
    },
    {
        id: 'prepare_anything',
        question: 'Do I need to prepare anything?',
        answer: [
            'No. Your advisor has your application, so there’s nothing to fill in beforehand.',
            'If you’re consolidating debts, it helps to have the lender names and amounts to hand - but you can do that on the call.',
        ],
    },
    {
        id: 'what_happens_after',
        question: 'What happens after the call?',
        answer: [
            'If you decide to go ahead, your advisor sets out their recommendation and next steps in writing so you can review everything in your own time.',
        ],
    },
];

export interface ContactCardProps {
    onBookCallClick: () => void;
    onMobileBookCallClick: () => void;
    showAdviceCallHeader?: boolean;
    isCallBookingVariantB?: boolean;
}

export const ContactCard = ({
    onBookCallClick,
    onMobileBookCallClick,
    isCallBookingVariantB,
}: ContactCardProps) => (
    <div
        className={
            'p-6 rounded-t-2xl rounded-bl-2xl bg-almond-300 flex flex-col items-center lg:w-400 mb-6'
        }
    >
        <div className='mb-2 text-sm'>We’ll call you soon from this number</div>
        <div className='flex flex-col bg-monochrome-100 rounded-lg p-4 w-full items-center justify-center mb-6'>
            <div className='flex items-center justify-center'>
                <PhoneCallIcon />
                <span className='ml-2 text-lg font-bold'>
                    {ADVISOR_PHONE_NUMBER}
                </span>
            </div>
            {isCallBookingVariantB && (
                <div className='mt-2 text-sm font-normal leading-18px tracking-normal text-midnight-400 font-inter-regular text-center'>
                    Mon - Thu, 8:30am - 7pm; Fri - Sat, 9am - 5pm; closed bank
                    holidays
                </div>
            )}
        </div>
        <div className='mb-2 text-sm'>Or if you prefer</div>
        <BookCallButton
            color='primary-flat'
            className='w-full'
            onClick={() =>
                getFinaliseDevice() === 'desktop'
                    ? onBookCallClick()
                    : onMobileBookCallClick()
            }
        />
    </div>
);

const FinaliseView = () => {
    const [showOfferCalendly, setShowOfferCalendly] = useState<boolean>(false);
    const [isInCallHours, setIsInCallHours] = useState(
        isWithinFinaliseCallHours,
    );

    const { applicationId } = useContext(ApplicationContext);
    const navigate = useNavigate();
    const callBookingVariant = useCallBookingVariant();
    const isCallBookingVariantB =
        callBookingVariant === ExperimentsConfig.Variants.B;

    const isInHoursCallFlowEnabled = window._env_.ENABLE_FINALISE_IN_HOURS_CALL;

    useEffect(() => {
        if (!isInHoursCallFlowEnabled) return;

        const updateCallHours = () =>
            setIsInCallHours(isWithinFinaliseCallHours());

        updateCallHours();

        const interval = window.setInterval(updateCallHours, 60 * 1000);

        return () => window.clearInterval(interval);
    }, [isInHoursCallFlowEnabled]);

    const successNavLink = '/eligibility/offer/confirmation';

    const showInHoursCallFlow = isInHoursCallFlowEnabled && isInCallHours;
    const advisorState: FinaliseAdvisorState = showInHoursCallFlow
        ? 'in_hours'
        : 'out_of_hours';

    const getTrackingProperties = () => ({
        ...getFinaliseTrackingProperties({
            advisorState,
            applicationId,
        }),
        experiment_name: callBookingVariant
            ? KAMELEOON_FEATURE.call_booking_redesign
            : null,
        variant: callBookingVariant || null,
    });

    const trackBookInstead = () => {
        mixpanel.track('tapped_book_instead', getTrackingProperties());
    };

    const handleBookCallClick = () => {
        trackBookInstead();
        setShowOfferCalendly(true);
    };

    const handleMobileBookCallClick = () => {
        trackBookInstead();
        navigate('/eligibility/offer/book-call');
    };

    const trackFaqOpened = (itemId: string) => {
        try {
            mixpanel.track('tapped_common_questions', {
                experiment_name: callBookingVariant
                    ? KAMELEOON_FEATURE.call_booking_redesign
                    : null,
                variant: callBookingVariant || null,
                application_id: applicationId,
                question: itemId,
            });
        } catch {
            console.error('Mixpanel tracking error, please try again later');
        }
    };

    if (callBookingVariant === null) return null;

    const headerText = isCallBookingVariantB
        ? 'Your advisor will call to finish your application'
        : 'Speak with your advisor to finish your application';

    return (
        <div className='flex flex-col mx-auto max-w-400 w-full lg:max-w-none'>
            <div className='hidden lg:block mb-8'>
                <BackButton />
            </div>
            <div className='px-0 lg:px-44'>
                <div
                    className={`lg:flex lg:items-start lg:justify-around lg:mt-20 ${
                        isCallBookingVariantB ? 'lg:mb-24' : 'lg:mb-40'
                    }`}
                >
                    <div
                        className={`flex flex-col items-center mt-5 lg:mt-0 mr-2 ${
                            isCallBookingVariantB ? 'lg:max-w-400' : ''
                        }`}
                    >
                        <div className='flex flex-col items-start'>
                            <p className='self-start leading-100 mb-2 font-sora-bold lg:w-auto text-28 lg:text-44 tracking-tighter text-midnight-600'>
                                Nearly there 🎉
                            </p>
                            <p className='leading-100 font-sora-bold mb-6 lg:w-96 text-2xl lg:text-28'>
                                {headerText}
                            </p>
                            <ul className='list-disc space-y-4 mb-8 leading-100 pl-5 text-body text-midnight-600'>
                                <li>Make sure this is right for you</li>
                                <li>See how it fits your budget</li>
                                <li>Get expert advice</li>
                            </ul>
                        </div>
                    </div>

                    <ContactCard
                        onBookCallClick={handleBookCallClick}
                        onMobileBookCallClick={handleMobileBookCallClick}
                        isCallBookingVariantB={isCallBookingVariantB}
                    />
                </div>
            </div>

            {isCallBookingVariantB && (
                <div className='mb-6 lg:mb-24'>
                    <FAQSection
                        items={faqItems}
                        title='Common questions'
                        onOpen={trackFaqOpened}
                    />
                </div>
            )}

            <div className='lg:px-16'>
                <TrustpilotReviews />
            </div>

            {showOfferCalendly && (
                <BookingCalendly
                    successNavLink={successNavLink}
                    onClose={() => setShowOfferCalendly(false)}
                />
            )}
        </div>
    );
};

export default FinaliseView;
